
import { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { UsersRound, Video, FolderHeart, Heart, RefreshCw } from 'lucide-react';
import { toast } from "sonner";
import { Button } from '@/components/ui/button';

interface ProfileStatsProps {
  user: User;
}

interface UserStats {
  followedCreators: number;
  availableVideos: number;
  playlists: number;
  favorites: number;
}

const ProfileStats = ({ user }: ProfileStatsProps) => {
  const [stats, setStats] = useState<UserStats>({
    followedCreators: 0,
    availableVideos: 0,
    playlists: 0,
    favorites: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchUserStats = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch number of followed creators from Supabase profile
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('profiles')
        .select('subscriptions')
        .eq('id', user.id)
        .single();
      
      if (subscriptionsError) {
        console.error('Error fetching subscriptions:', subscriptionsError);
      }
      
      const followedCreators = subscriptionsData?.subscriptions?.length || 0;
      
      // Fetch number of available videos from Supabase database (using both tables)
      const [videosResult, cachedVideosResult] = await Promise.all([
        supabase.from('videos').select('*', { count: 'exact', head: true }),
        supabase.from('cached_videos_feed').select('*', { count: 'exact', head: true })
      ]);
      
      const videosCount = (videosResult.count || 0) + (cachedVideosResult.count || 0);
      
      if (videosResult.error) {
        console.error('Error fetching videos count:', videosResult.error);
      }
      if (cachedVideosResult.error) {
        console.error('Error fetching cached videos count:', cachedVideosResult.error);
      }
      
      // Fetch number of playlists from Supabase
      const { count: playlistsCount, error: playlistsError } = await supabase
        .from('playlists')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (playlistsError) {
        console.error('Error fetching playlists count:', playlistsError);
      }
      
      // Fetch number of favorites from Supabase
      const { count: favoritesCount, error: favoritesError } = await supabase
        .from('favorites')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (favoritesError) {
        console.error('Error fetching favorites count:', favoritesError);
      }
      
      console.log('Stats from Supabase database:', {
        followedCreators,
        videosCount,
        playlistsCount,
        favoritesCount
      });
      
      setStats({
        followedCreators,
        availableVideos: videosCount,
        playlists: playlistsCount || 0,
        favorites: favoritesCount || 0
      });
      
      toast.success('Statistiques actualisées depuis la base de données');
    } catch (error) {
      console.error('Error fetching user stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchUserStats();
  }, [user]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUserStats();
  };

  const statCards = [
    {
      title: 'Créateurs suivis',
      value: stats.followedCreators,
      icon: <UsersRound className="h-6 w-6 text-blue-500" />
    },
    {
      title: 'Vidéos disponibles',
      value: stats.availableVideos,
      icon: <Video className="h-6 w-6 text-red-500" />
    },
    {
      title: 'Playlists',
      value: stats.playlists,
      icon: <FolderHeart className="h-6 w-6 text-purple-500" />
    },
    {
      title: 'Favoris',
      value: stats.favorites,
      icon: <Heart className="h-6 w-6 text-yellow-500" />
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Statistiques</h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh} 
          disabled={refreshing || loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-gray-200 dark:border-gray-800 shadow-sm">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.title}</p>
                  <h2 className="text-3xl font-bold mt-1">
                    {loading || refreshing ? (
                      <span className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-8 w-8 inline-block"></span>
                    ) : (
                      stat.value
                    )}
                  </h2>
                </div>
                <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-full">
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProfileStats;
